import { Fragment, useEffect, useMemo, useState } from 'react'
import api from '../api/axios'
import EmployeeIdChangePanel, { type EmployeeIdTarget } from './EmployeeIdChangePanel'
import {
  ArrowPathIcon,
  MagnifyingGlassIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  KeyIcon,
  PlusIcon,
  ShieldCheckIcon,
  UserCircleIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline'

interface Company {
  id: string
  company_name: string
  company_code: string
  database_name: string
  zitadel_org_id?: string
}

interface UserEntry {
  email: string
  name: string | null
  registry: {
    id: number
    role: string
    is_active: boolean
    designated_owner: boolean
    bootstrap_only: boolean
    is_company_owner_legacy: boolean
  } | null
  tenant: {
    id: number
    employee_id: string
    role: string
    is_active: boolean
  } | null
}

interface AnalyzeResponse {
  company: {
    id: number
    company_name: string
    company_code: string
    database_name: string
    zitadel_org_id?: string
    zitadel_project_id?: string
    zitadel_client_id?: string
  }
  users: UserEntry[]
  suggestion: { owner: string | null; admins: string[] }
  tenantError: string | null
}

interface ApplyResponse {
  company_id: number
  owner: string
  admins: string[]
  steps: Array<{ action: string; email?: string; role?: string; count?: number; orgOwner?: boolean }>
  warnings: string[]
}

interface ReissueResponse {
  company_code: string
  owner: string
  admins: string[]
  tenant_users: Array<{ email: string; role: string; created: boolean; employee_id: string | null }>
  access_reissued_at: string
  epoch_saved: boolean
  caches_cleared: { resolution: number; synced: number }
  sessions: { requested: boolean; revoked: number; accounts: number }
  warnings: string[]
}

interface PersonHit {
  user_id: number
  name: string
  email: string | null
  role: string
  is_active: boolean
  employee_id: string | null
}

const REISSUE_CONFIRM =
  'Rebuilds owner and admin access in this company and makes every server forget old sign-ins within 30 seconds. Use after Clean DB.'

interface Props {
  company: Company
  onClose: () => void
  onApplied?: () => void
}

export default function CompanyAccessManagerModal({ company, onClose, onApplied }: Props) {
  const [loading, setLoading] = useState(false)
  const [analyze, setAnalyze] = useState<AnalyzeResponse | null>(null)
  const [error, setError] = useState('')

  const [ownerEmail, setOwnerEmail] = useState<string | null>(null)
  const [adminEmails, setAdminEmails] = useState<Set<string>>(new Set())
  const [addEmail, setAddEmail] = useState('')
  const [extraUsers, setExtraUsers] = useState<UserEntry[]>([])
  const [demoteOthers, setDemoteOthers] = useState(false)

  const [applying, setApplying] = useState(false)
  const [applyResult, setApplyResult] = useState<ApplyResponse | null>(null)

  const [reissuing, setReissuing] = useState(false)
  const [reissueResult, setReissueResult] = useState<ReissueResponse | null>(null)
  const [revokeSessions, setRevokeSessions] = useState(false)

  // Change employee ID: which row's panel is open ('row:<email>' or 'hit:<userId>')
  const [idPanel, setIdPanel] = useState<{ key: string; target: EmployeeIdTarget } | null>(null)
  const [findQuery, setFindQuery] = useState('')
  const [finding, setFinding] = useState(false)
  const [hits, setHits] = useState<PersonHit[] | null>(null)

  useEffect(() => {
    void run()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const run = async () => {
    setLoading(true)
    setError('')
    try {
      const { data } = await api.get<AnalyzeResponse>(`/super-admin/companies/${company.id}/access-manager`)
      setAnalyze(data)
      setOwnerEmail(data.suggestion?.owner || null)
      setAdminEmails(new Set(data.suggestion?.admins || []))
    } catch (e: any) {
      setError(e.response?.data?.message || 'Failed to load access state')
    } finally {
      setLoading(false)
    }
  }

  const allUsers = useMemo<UserEntry[]>(() => {
    if (!analyze) return extraUsers
    const seen = new Set(analyze.users.map(u => u.email.toLowerCase()))
    const extras = extraUsers.filter(u => !seen.has(u.email.toLowerCase()))
    return [...analyze.users, ...extras]
  }, [analyze, extraUsers])

  const handleAddEmail = () => {
    const email = addEmail.trim().toLowerCase()
    if (!email) return
    if (!email.includes('@')) {
      setError('Enter a valid email address')
      return
    }
    setError('')
    if (allUsers.some(u => u.email.toLowerCase() === email)) {
      setAddEmail('')
      return
    }
    setExtraUsers(prev => [...prev, { email, name: null, registry: null, tenant: null }])
    setAddEmail('')
  }

  const setOwner = (email: string) => {
    setOwnerEmail(email)
    setAdminEmails(prev => {
      const next = new Set(prev)
      next.delete(email)
      return next
    })
  }

  const toggleAdmin = (email: string) => {
    if (email === ownerEmail) return
    setAdminEmails(prev => {
      const next = new Set(prev)
      if (next.has(email)) next.delete(email)
      else next.add(email)
      return next
    })
  }

  const handleApply = async () => {
    if (!ownerEmail) {
      setError('Pick an owner before applying')
      return
    }
    const summary =
      `Apply access changes for "${company.company_name}"?\n\n` +
      `• Owner: ${ownerEmail}\n` +
      `• Admins: ${adminEmails.size === 0 ? '(none)' : Array.from(adminEmails).join(', ')}\n` +
      (demoteOthers ? `• Demote any other current owners/admins to manager\n` : '') +
      `\nUpdates tenant DB users, registry rows, and Zitadel grants. Idempotent.`
    if (!confirm(summary)) return

    setApplying(true)
    setError('')
    setApplyResult(null)
    try {
      const { data } = await api.post<ApplyResponse>(
        `/super-admin/companies/${company.id}/access-manager/apply`,
        {
          ownerEmail,
          adminEmails: Array.from(adminEmails),
          demoteOthers,
        },
      )
      setApplyResult(data)
      onApplied?.()
      // Refresh analyze to reflect new state
      await run()
    } catch (e: any) {
      setError(e.response?.data?.message || 'Apply failed')
    } finally {
      setApplying(false)
    }
  }

  const handleFind = async () => {
    const q = findQuery.trim()
    if (q.length < 2) return
    setFinding(true)
    setError('')
    try {
      const { data } = await api.get<{ users: PersonHit[] }>(
        `/super-admin/companies/${company.id}/users/search`,
        { params: { q } },
      )
      setHits(data.users)
    } catch (e: any) {
      setError(e.response?.data?.message || 'Search failed')
    } finally {
      setFinding(false)
    }
  }

  // After a change: refresh the people list (and search hits) quietly.
  const handleIdChanged = async (userId: number | undefined, newId: string) => {
    setHits(prev => prev?.map(h => (h.user_id === userId ? { ...h, employee_id: newId } : h)) ?? prev)
    try {
      const { data } = await api.get<AnalyzeResponse>(`/super-admin/companies/${company.id}/access-manager`)
      setAnalyze(data)
    } catch { /* list refresh only */ }
  }

  const changeIdLink = (key: string, target: EmployeeIdTarget) => (
    <div className="text-xs text-slate-500 mt-0.5">
      Employee ID: <span className="font-mono text-slate-700">{target.employeeId || '(none)'}</span>
      {' · '}
      <button
        onClick={() => setIdPanel(idPanel?.key === key ? null : { key, target })}
        className="text-indigo-600 hover:underline font-medium"
      >
        Change
      </button>
    </div>
  )

  // Re-applies the company's CURRENT owner + admins (no selection needed) and
  // bumps the server's access epoch, so stale cached sign-ins are dropped.
  const handleReissue = async () => {
    if (!confirm(REISSUE_CONFIRM)) return
    setReissuing(true)
    setError('')
    setReissueResult(null)
    try {
      const { data } = await api.post<ReissueResponse>(
        `/super-admin/companies/${company.id}/access-manager/reissue`,
        { revokeSessions },
      )
      setReissueResult(data)
      onApplied?.()
      await run()
    } catch (e: any) {
      setError(e.response?.data?.message || 'Reissue failed')
    } finally {
      setReissuing(false)
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4" onClick={onClose}>
      <div
        className="bg-white rounded-xl shadow-xl border border-slate-200 w-full max-w-3xl max-h-[90vh] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-xl bg-indigo-600 flex items-center justify-center">
              <KeyIcon className="h-5 w-5 text-white" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-slate-900">Company Access Manager</h2>
              <p className="text-xs text-slate-500">{company.company_name} ({company.company_code})</p>
            </div>
          </div>
          <button onClick={onClose} className="p-1 rounded-md hover:bg-slate-100 transition-colors">
            <XMarkIcon className="w-5 h-5 text-slate-400" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-4">
          {loading && (
            <div className="flex items-center gap-2 text-slate-500 text-sm">
              <ArrowPathIcon className="w-4 h-4 animate-spin" /> Analyzing current access…
            </div>
          )}

          {error && (
            <div className="flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded-md text-sm text-red-700">
              <ExclamationTriangleIcon className="w-4 h-4 flex-shrink-0 mt-0.5" />
              {error}
            </div>
          )}

          {analyze?.tenantError && (
            <div className="flex items-start gap-2 p-3 bg-amber-50 border border-amber-200 rounded-md text-sm text-amber-800">
              <ExclamationTriangleIcon className="w-4 h-4 flex-shrink-0 mt-0.5" />
              <div>
                <div className="font-semibold">Tenant DB unreachable</div>
                <div>{analyze.tenantError}</div>
              </div>
            </div>
          )}

          {!loading && analyze && (
            <>
              <div className="text-xs text-slate-500">
                Pick exactly one <span className="font-semibold text-indigo-700">Owner</span> (full tenant control + Zitadel
                ORG_OWNER) and any number of <span className="font-semibold text-blue-700">Admins</span>.
                {!analyze.company.zitadel_org_id && (
                  <span className="block mt-1 text-amber-700">
                    This company is not provisioned on Zitadel — Zitadel grants will be skipped.
                  </span>
                )}
              </div>

              {/* User list */}
              <div className="border border-slate-200 rounded-lg overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 text-xs uppercase text-slate-500 tracking-wide">
                    <tr>
                      <th className="text-left px-4 py-2 font-semibold">Email</th>
                      <th className="text-left px-4 py-2 font-semibold">Current</th>
                      <th className="text-center px-3 py-2 font-semibold w-20">Owner</th>
                      <th className="text-center px-3 py-2 font-semibold w-20">Admin</th>
                    </tr>
                  </thead>
                  <tbody>
                    {allUsers.length === 0 && (
                      <tr>
                        <td colSpan={4} className="px-4 py-6 text-center text-sm text-slate-400">
                          No users found. Add one below.
                        </td>
                      </tr>
                    )}
                    {allUsers.map((u) => {
                      const isOwner = ownerEmail === u.email
                      const isAdmin = adminEmails.has(u.email)
                      const tenantBadge = u.tenant?.role
                      const registryBadge = u.registry?.role
                      const rowKey = `row:${u.email.toLowerCase()}`
                      return (
                        <Fragment key={u.email}>
                        <tr className="border-t border-slate-100">
                          <td className="px-4 py-2.5">
                            <div className="font-medium text-slate-900">{u.email}</div>
                            {u.name && <div className="text-xs text-slate-500">{u.name}</div>}
                            {u.tenant &&
                              changeIdLink(rowKey, {
                                userId: u.tenant.id,
                                email: u.email,
                                name: u.name,
                                employeeId: u.tenant.employee_id,
                              })}
                          </td>
                          <td className="px-4 py-2.5">
                            <div className="flex flex-wrap gap-1 text-[11px]">
                              {tenantBadge && (
                                <span className="px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-700 border border-emerald-200">
                                  tenant: {tenantBadge}
                                </span>
                              )}
                              {registryBadge && (
                                <span className="px-1.5 py-0.5 rounded bg-violet-50 text-violet-700 border border-violet-200">
                                  registry: {registryBadge}
                                </span>
                              )}
                              {u.registry?.designated_owner && (
                                <span className="px-1.5 py-0.5 rounded bg-amber-50 text-amber-700 border border-amber-200">
                                  designated_owner
                                </span>
                              )}
                              {!u.registry && !u.tenant && (
                                <span className="px-1.5 py-0.5 rounded bg-slate-100 text-slate-500 border border-slate-200">
                                  new
                                </span>
                              )}
                            </div>
                          </td>
                          <td className="text-center px-3 py-2.5">
                            <input
                              type="radio"
                              name="owner"
                              checked={isOwner}
                              onChange={() => setOwner(u.email)}
                              className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 cursor-pointer"
                            />
                          </td>
                          <td className="text-center px-3 py-2.5">
                            <input
                              type="checkbox"
                              checked={isAdmin}
                              disabled={isOwner}
                              onChange={() => toggleAdmin(u.email)}
                              className="h-4 w-4 text-blue-600 focus:ring-blue-500 cursor-pointer disabled:opacity-30"
                            />
                          </td>
                        </tr>
                        {idPanel?.key === rowKey && (
                          <tr>
                            <td colSpan={4} className="px-4 pb-3">
                              <EmployeeIdChangePanel
                                companyId={company.id}
                                target={idPanel.target}
                                onClose={() => setIdPanel(null)}
                                onChanged={(newId) => void handleIdChanged(idPanel.target.userId, newId)}
                              />
                            </td>
                          </tr>
                        )}
                        </Fragment>
                      )
                    })}
                  </tbody>
                </table>
              </div>

              {/* Add new email */}
              <div className="flex items-center gap-2">
                <input
                  type="email"
                  placeholder="Add user by email (will be created in tenant DB on apply)"
                  value={addEmail}
                  onChange={(e) => setAddEmail(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') { e.preventDefault(); handleAddEmail() }
                  }}
                  className="flex-1 px-3 py-2 text-sm border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
                <button
                  onClick={handleAddEmail}
                  className="inline-flex items-center gap-1 px-3 py-2 text-sm font-semibold text-white bg-indigo-600 hover:bg-indigo-700 rounded-md"
                >
                  <PlusIcon className="w-4 h-4" /> Add
                </button>
              </div>

              {/* Find any person (employees who aren't owner/admin) */}
              <div className="border border-slate-200 rounded-lg p-3 space-y-2">
                <div className="text-sm font-semibold text-slate-800">Find a person</div>
                <div className="text-xs text-slate-500">
                  Anyone in this company, by email, name or employee ID, to change their employee ID.
                </div>
                <div className="flex items-center gap-2">
                  <input
                    type="text"
                    placeholder="Email, name or employee ID"
                    value={findQuery}
                    onChange={(e) => setFindQuery(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); void handleFind() } }}
                    className="flex-1 px-3 py-2 text-sm border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  />
                  <button
                    onClick={() => void handleFind()}
                    disabled={finding || findQuery.trim().length < 2}
                    className="inline-flex items-center gap-1 px-3 py-2 text-sm font-medium text-indigo-700 bg-white border border-indigo-300 rounded-md hover:bg-indigo-50 disabled:opacity-50"
                  >
                    {finding ? <ArrowPathIcon className="w-4 h-4 animate-spin" /> : <MagnifyingGlassIcon className="w-4 h-4" />}
                    Find
                  </button>
                </div>
                {hits && hits.length === 0 && <div className="text-xs text-slate-400">No one matches.</div>}
                {hits && hits.length > 0 && (
                  <ul className="divide-y divide-slate-100">
                    {hits.map((h) => {
                      const key = `hit:${h.user_id}`
                      const target: EmployeeIdTarget = { userId: h.user_id, email: h.email, name: h.name, employeeId: h.employee_id }
                      return (
                        <li key={h.user_id} className="py-2">
                          <div className="text-sm text-slate-900">
                            {h.name}
                            <span className="text-xs text-slate-500">
                              {' · '}{h.role}{h.email ? ` · ${h.email}` : ''}{h.is_active ? '' : ' · inactive'}
                            </span>
                          </div>
                          {changeIdLink(key, target)}
                          {idPanel?.key === key && (
                            <EmployeeIdChangePanel
                              companyId={company.id}
                              target={idPanel.target}
                              onClose={() => setIdPanel(null)}
                              onChanged={(newId) => void handleIdChanged(h.user_id, newId)}
                            />
                          )}
                        </li>
                      )
                    })}
                  </ul>
                )}
              </div>

              {/* Demote toggle */}
              <label className="flex items-start gap-2 text-sm text-slate-700 select-none">
                <input
                  type="checkbox"
                  checked={demoteOthers}
                  onChange={(e) => setDemoteOthers(e.target.checked)}
                  className="mt-0.5 h-4 w-4 text-rose-600 focus:ring-rose-500"
                />
                <span>
                  <span className="font-medium">Demote others.</span>{' '}
                  <span className="text-slate-500">
                    Anyone currently holding <code>owner</code>/<code>admin</code> in the tenant DB but not selected
                    above is reset to <code>manager</code>. Default off — additive.
                  </span>
                </span>
              </label>

              {/* Apply result */}
              {applyResult && (
                <div className="border border-emerald-200 bg-emerald-50 rounded-lg p-4">
                  <div className="flex items-center gap-2 text-emerald-800 font-semibold">
                    <CheckCircleIcon className="w-5 h-5" /> Applied
                  </div>
                  <div className="mt-2 text-xs text-emerald-900">
                    Owner: <span className="font-mono">{applyResult.owner}</span>
                    {' · '}Admins: <span className="font-mono">{applyResult.admins.join(', ') || '—'}</span>
                    {' · '}Steps: {applyResult.steps.length}
                  </div>
                  {applyResult.warnings.length > 0 && (
                    <ul className="mt-2 text-xs text-amber-700 list-disc list-inside space-y-0.5">
                      {applyResult.warnings.map((w, i) => <li key={i}>{w}</li>)}
                    </ul>
                  )}
                </div>
              )}
            </>
          )}

          {/* Reissue access */}
          <div className="border border-slate-200 rounded-lg p-4 space-y-2">
            <div className="flex items-center justify-between gap-3">
              <div>
                <div className="text-sm font-semibold text-slate-800">Reissue access</div>
                <div className="text-xs text-slate-500">
                  Re-applies the current owner and admins, no selection needed. Use after Clean DB, or when an admin
                  is stuck on an old account.
                </div>
              </div>
              <button
                onClick={handleReissue}
                disabled={reissuing || loading || applying}
                className="shrink-0 inline-flex items-center gap-2 px-3 py-2 text-sm font-medium text-indigo-700 bg-white border border-indigo-300 rounded-md hover:bg-indigo-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {reissuing ? <ArrowPathIcon className="w-4 h-4 animate-spin" /> : <ArrowPathIcon className="w-4 h-4" />}
                {reissuing ? 'Reissuing…' : 'Reissue access'}
              </button>
            </div>
            <label className="flex items-start gap-2 text-xs text-slate-600 select-none">
              <input
                type="checkbox"
                checked={revokeSessions}
                onChange={(e) => setRevokeSessions(e.target.checked)}
                className="mt-0.5 h-4 w-4 text-indigo-600 focus:ring-indigo-500"
              />
              <span>
                Also sign them out now (ends their sessions everywhere, including other companies they belong to).
              </span>
            </label>

            {reissueResult && (
              <div className="border border-emerald-200 bg-emerald-50 rounded-lg p-3">
                <div className="flex items-center gap-2 text-emerald-800 font-semibold text-sm">
                  <CheckCircleIcon className="w-5 h-5" /> Access reissued
                </div>
                <ul className="mt-2 text-xs text-emerald-900 space-y-0.5">
                  {reissueResult.tenant_users.map((u) => (
                    <li key={u.email}>
                      <span className="font-mono">{u.email}</span> · {u.role} ·{' '}
                      {u.created ? 'account re-created' : 'existing account kept'}
                      {u.employee_id && (
                        <>
                          {' '}· ID <span className="font-mono">{u.employee_id}</span>
                        </>
                      )}
                    </li>
                  ))}
                </ul>
                <div className="mt-2 text-xs text-emerald-900">
                  {reissueResult.epoch_saved
                    ? 'Every server forgets old sign-ins for this company within 30 seconds.'
                    : 'This server forgot old sign-ins now; the others catch up within 5 minutes.'}
                  {reissueResult.sessions.requested &&
                    ` Signed out ${reissueResult.sessions.revoked} session${reissueResult.sessions.revoked === 1 ? '' : 's'}.`}
                </div>
                <div className="mt-2 text-xs text-slate-700">
                  Ask them to sign out of the admin portal and sign back in.
                </div>
                {reissueResult.warnings.length > 0 && (
                  <ul className="mt-2 text-xs text-amber-700 list-disc list-inside space-y-0.5">
                    {reissueResult.warnings.map((w, i) => <li key={i}>{w}</li>)}
                  </ul>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-6 py-4 border-t border-slate-200 bg-slate-50">
          <div className="text-xs text-slate-500 flex items-center gap-3">
            {ownerEmail ? (
              <span className="flex items-center gap-1">
                <UserCircleIcon className="w-4 h-4 text-indigo-600" />
                Owner: <span className="font-mono">{ownerEmail}</span>
              </span>
            ) : (
              <span className="text-amber-700">No owner selected</span>
            )}
            {adminEmails.size > 0 && (
              <span className="flex items-center gap-1">
                <ShieldCheckIcon className="w-4 h-4 text-blue-600" />
                {adminEmails.size} admin{adminEmails.size === 1 ? '' : 's'}
              </span>
            )}
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-slate-700 bg-white border border-slate-300 rounded-md hover:bg-slate-50"
            >
              Close
            </button>
            <button
              onClick={handleApply}
              disabled={applying || !ownerEmail || loading}
              className="inline-flex items-center gap-2 px-4 py-2 text-sm font-semibold text-white bg-indigo-600 hover:bg-indigo-700 rounded-md disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {applying && <ArrowPathIcon className="w-4 h-4 animate-spin" />}
              {applying ? 'Applying…' : 'Apply Access'}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
